// Индекс терминов глоссария для сквозной подсветки (LEARNING-SYSTEM §8).
//
// Чистый модуль без React и без PowerSync: на вход — плоский список терминов,
// на выход — скомпилированный `RegExp` + Map для резолва совпадения в термин.
//
// Почему НЕ `\b`: словесная граница в JS определена через `\w` = [A-Za-z0-9_],
// поэтому «производная» в кириллическом тексте не матчится корректно, а внутри
// LaTeX (`\frac`, `x_1`) `\b` даёт ложные срабатывания. Используем явные
// lookaround'ы по Unicode-классам (`\p{L}`, `\p{N}`) с флагом `u`. Существующий
// TipTap-марк (glossary-tooltip-mark.ts) всё ещё на `\b` — это известный баг,
// сюда его не переносим.

import { GLOSSARY_MAX_MARKS_PER_DOC } from "@studyhub/shared/learning";

/** Термин в форме, достаточной для подсветки и поповера. */
export interface GlossaryTermLite {
	id: string;
	term: string;
	definition: string;
	importance: "high" | "medium" | "low" | null;
	status: string | null;
	materialId: string | null;
	subsectionId: string | null;
	/** Первая цитата термина — источник для CitationChip в поповере. */
	citation: { page?: number; timestamp?: number; snippet?: string } | null;
}

export interface GlossaryIndex {
	/** `null` — подсвечивать нечего (пустой словарь или невалидный паттерн). */
	regex: RegExp | null;
	byLower: Map<string, GlossaryTermLite>;
	/** Сколько терминов реально попало в индекс. */
	size: number;
	/** true — словарь урезали до `importance:'high'` (см. GLOSSARY_INDEX_MAX_TERMS). */
	degraded: boolean;
}

/**
 * Потолок терминов в одном регулярном выражении. Выше — альтернация из тысяч
 * веток начинает стоить дороже самого рендера (и рискует катастрофическим
 * бэктрекингом на длинных транскриптах), поэтому словарь ужимается до
 * `importance: 'high'`.
 */
export const GLOSSARY_INDEX_MAX_TERMS = 800;

/** Односимвольные «термины» замусоривают текст — в индекс не берём. */
const MIN_TERM_LENGTH = 2;

export const EMPTY_GLOSSARY_INDEX: GlossaryIndex = {
	regex: null,
	byLower: new Map(),
	size: 0,
	degraded: false,
};

/** Экранирование только SyntaxCharacter — в режиме `u` прочие escape запрещены. */
export function escapeForRegExp(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Компиляция индекса. Ключи сортируются по УБЫВАНИЮ длины, чтобы длинный
 * термин («граф знаний») выигрывал у короткого («граф»).
 */
export function buildGlossaryIndex(terms: readonly GlossaryTermLite[]): GlossaryIndex {
	const usable = terms.filter(
		(t) => typeof t.term === "string" && t.term.trim().length >= MIN_TERM_LENGTH,
	);

	let selected = usable;
	let degraded = false;
	if (usable.length > GLOSSARY_INDEX_MAX_TERMS) {
		degraded = true;
		// Деградация громкая, но не через console: флаг `degraded` уходит в
		// возвращаемый индекс — потребитель может показать это в UI/аналитике.
		selected = usable.filter((t) => t.importance === "high").slice(0, GLOSSARY_INDEX_MAX_TERMS);
	}

	const byLower = new Map<string, GlossaryTermLite>();
	for (const term of selected) {
		const key = term.term.trim().toLowerCase();
		if (key.length < MIN_TERM_LENGTH) continue;
		// Первый выигрывает: список приходит уже отсортированным по приоритету
		// (см. use-glossary-terms.ts — manual > verified > edited > ai-generated).
		if (!byLower.has(key)) byLower.set(key, term);
	}

	if (byLower.size === 0) return EMPTY_GLOSSARY_INDEX;

	const alternation = [...byLower.keys()]
		.sort((a, b) => b.length - a.length)
		.map(escapeForRegExp)
		.join("|");

	let regex: RegExp | null = null;
	try {
		regex = new RegExp(`(?<![\\p{L}\\p{N}_])(${alternation})(?![\\p{L}\\p{N}_])`, "giu");
	} catch {
		// Экзотический термин сломал паттерн — молча выключаем слой, а не падаем.
		regex = null;
	}

	return { regex, byLower, size: byLower.size, degraded };
}

/**
 * Бюджет отметок документа: «только первое вхождение» + потолок на документ
 * (§8.4 — это дизайн-требование, не микрооптимизация).
 */
export interface GlossaryMarkBudget {
	max: number;
	used: number;
	seen: Set<string>;
}

export function createMarkBudget(max: number = GLOSSARY_MAX_MARKS_PER_DOC): GlossaryMarkBudget {
	return { max, used: 0, seen: new Set() };
}

/** Разрешить отметку термина (true) либо отказать — повтор/исчерпан потолок. */
export function takeMark(budget: GlossaryMarkBudget, lowerTerm: string): boolean {
	if (budget.used >= budget.max) return false;
	if (budget.seen.has(lowerTerm)) return false;
	budget.seen.add(lowerTerm);
	budget.used += 1;
	return true;
}

/**
 * Класс отметки. Пунктир снизу и `cursor: help` — без изменения цвета текста и
 * без фона: при 20 терминах любая заливка превращает страницу в пестроту.
 *
 * Толщина и цвет подобраны так, чтобы отметку было ВИДНО, но она не спорила с
 * текстом: `--line-2` на светлом фоне читался почти как отсутствие подчёркивания
 * (жалоба 2026-08-09 — «практически не вижу выделение»). Канон один на обе
 * поверхности слоя: этот класс и марк TipTap (`glossary-tooltip-mark.ts`)
 * обязаны совпадать, иначе один и тот же термин в конспекте и в чате выглядит
 * по-разному.
 */
export const GLOSSARY_MARK_CLASS =
	"underline decoration-dashed decoration-[1.5px] underline-offset-[3px] " +
	"decoration-muted-foreground/60 cursor-help transition-colors " +
	"hover:decoration-primary hover:text-foreground";
